import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import * as VKID from '@vkid/sdk';
import { getUserStore } from '@ai/common/src/store/UserStore';
import { constantsStore } from '@ai/common/src/store/ConstantsStore';
import { IUser, VkLoginSuccessData } from '@ai/common/src/types/user';
import { generatePkceParams, PkceParams } from '@utils/oauthUtils';
import { VK_REDIRECT_PAGE } from './const';
import { Language } from '@ai/common/src/types/language';

export const AuthVK = observer(
  ({
    lang,
    navigateAfterLogin,
  }: {
    navigateAfterLogin: (user: IUser, balance: boolean) => void;
    lang: Language;
  }) => {
    const [pkceParams, setPkceParams] = useState<PkceParams | null>(null);
    const { loginWithVk } = getUserStore('platform');

    useEffect(() => {
      const callGeneratePkceParams = async () => {
        setPkceParams(await generatePkceParams());
      };

      callGeneratePkceParams();
    }, []);

    // Контейнер для кнопки VK ID
    const buttonLoginWithVkRef = useRef<HTMLDivElement>(null);
    const widgetRef = useRef<VKID.OneTap | null>(null);

    // Создание экземпляра кнопки VK ID
    const oneTap = useMemo(() => {
      if (constantsStore.authConstants?.vkId && pkceParams) {
        try {
          const n = +constantsStore.authConstants?.vkId.clientId;
          if (isNaN(n)) {
            console.error('vkId not found in authConstants');

            return null;
          }
          VKID.Config.init({
            app: n,
            redirectUrl: `${window.location.origin}/${VK_REDIRECT_PAGE}`,
            responseMode: VKID.ConfigResponseMode.Callback,
            source: VKID.ConfigSource.LOWCODE,
            scope: 'email',
            mode: VKID.ConfigAuthMode.InNewWindow,
            state: pkceParams.state,
            codeChallenge: pkceParams.code_challenge,
          });

          return new VKID.OneTap();
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      return null;
    }, [pkceParams]);

    const handleError = (error: unknown) => {
      console.error(error);
    };

    const handleVkLoginSuccess = useCallback(
      async (vkData: VkLoginSuccessData) => {
        if (pkceParams) {
          try {
            const { currentUser, hasBalance } = await loginWithVk({
              ...vkData,
              state: pkceParams.state,
              code_verifier: pkceParams.code_verifier,
            });
            navigateAfterLogin(currentUser, hasBalance);
          } catch (error) {
            console.error(error);
          }
        }
      },
      [loginWithVk, navigateAfterLogin, pkceParams]
    );

    useEffect(() => {
      if (buttonLoginWithVkRef.current && oneTap) {
        if (widgetRef.current) {
          widgetRef.current.off(VKID.WidgetEvents.ERROR, handleError);
          widgetRef.current.off(VKID.OneTapInternalEvents.LOGIN_SUCCESS, handleVkLoginSuccess);
          widgetRef.current = null;
        }
        if (buttonLoginWithVkRef.current.innerHTML) {
          buttonLoginWithVkRef.current.innerHTML = '';
        }

        // Отрисовка кнопки в контейнере
        widgetRef.current = oneTap
          .render({
            container: buttonLoginWithVkRef.current,
            scheme: VKID.Scheme.LIGHT,
            lang: lang === 'ru' ? VKID.Languages.RUS : VKID.Languages.ENG,
            skin: VKID.OneTapSkin.Secondary,
            styles: { height: 40 },
          })
          .on(VKID.WidgetEvents.ERROR, handleError)
          .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, handleVkLoginSuccess);
      }
    }, [oneTap, lang, handleVkLoginSuccess]);

    return <div ref={buttonLoginWithVkRef}></div>;
  }
);
